<template>
  <div class="container home">
    <div class="home-shell">
      <div class="bg-blobs">
        <div class="blob blob-1"></div>
        <div class="blob blob-2"></div>
        <div class="blob blob-3"></div>
        <div class="blob blob-4"></div>
      </div>
      <main class="hero">
        <Wordmark />

        <span class="pill">
          <svg class="ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 2l2.4 7.4H22l-6.2 4.5L18.2 22 12 17.5 5.8 22l2.4-8.1L2 9.4h7.6L12 2z" />
          </svg>
          <Transition :name="transitionName" mode="out-in">
            <span :key="promptIndex" class="pill-text">{{ currentPrompt }}</span>
          </Transition>
        </span>

        <ScrawlDemo class="diorama" />
      </main>

      <footer class="bottom">
        <ActMarker class="act-marker" />
        <div class="cta">
          <a href="#signup" class="btn btn-primary">Get Started</a>
          <a href="#login" class="btn btn-secondary">Log In</a>
        </div>

        <nav class="legal-links" aria-label="Site information">
          <a href="/how-it-works.html">How it works</a>
          <span aria-hidden="true">·</span>
          <a href="/about.html">About</a>
          <span aria-hidden="true">·</span>
          <a href="/terms.html">Terms</a>
          <span aria-hidden="true">·</span>
          <span class="copyright">© 2026 Scrawl</span>
        </nav>
      </footer>

      <InstallBanner />
    </div>
  </div>

  <!-- Open-in-app strip: shown only when getInstalledRelatedApps() confirms
       the PWA is installed but the user is browsing in Chrome (not standalone).
       Chrome intercepts the navigation triggered by "Open app" via scope
       matching and switches to the installed PWA. Dismissed per-session via
       sessionStorage so it reappears on the next browser visit. -->
  <Teleport to="body">
    <div v-if="openInAppVisible" class="open-in-app-strip" role="alert" aria-live="polite">
      <span class="oap-dot" aria-hidden="true"></span>
      <span class="oap-text">Scrawl is on your home screen</span>
      <button type="button" class="oap-open" @click="openInApp">Open app</button>
      <button type="button" class="oap-close" aria-label="Dismiss" @click="dismissOpenInApp">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true">
          <line x1="6" y1="6" x2="18" y2="18"/>
          <line x1="18" y1="6" x2="6" y2="18"/>
        </svg>
      </button>
    </div>
  </Teleport>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { getShuffledHeroPrompts } from "@/utils/heroPrompts";
import { isStandalone } from "@/utils/installEligibility";
import ScrawlDemo from "@/components/ScrawlDemo.vue";
import Wordmark from "@/components/Wordmark.vue";
import ActMarker from "@/components/ActMarker.vue";

const PROMPT_TRANSITIONS = [
  "prompt-up",
  "prompt-down",
  "prompt-fade",
  "prompt-blur",
  "prompt-pop",
  "prompt-wipe",
];

export default {
  name: "home",
  components: {
    ScrawlDemo,
    Wordmark,
    ActMarker,
    // Lazy-loaded — install banner is conditional UI (suppressed for installed
    // users, dismissed users within their cooldown, and unsupported platforms)
    // and ships its own large SVG illustrations + carousel CSS.
    InstallBanner: defineAsyncComponent(() =>
      import("@/components/InstallBanner.vue")
    ),
  },
  data() {
    return {
      prompts: [],
      promptIndex: 0,
      transitionName: "prompt-up",
      openInAppVisible: false,
    };
  },
  computed: {
    currentPrompt() {
      if (!this.prompts.length) return "";
      return this.prompts[this.promptIndex % this.prompts.length];
    },
  },
  created() {
    this.prompts = getShuffledHeroPrompts();
    this.checkInstalledPWA();
  },
  mounted() {
    this.promptTimer = setInterval(() => {
      let nextIdx;
      do {
        nextIdx = Math.floor(Math.random() * PROMPT_TRANSITIONS.length);
      } while (PROMPT_TRANSITIONS[nextIdx] === this.transitionName);
      this.transitionName = PROMPT_TRANSITIONS[nextIdx];
      this.promptIndex++;
    }, 4500);
  },
  beforeUnmount() {
    clearInterval(this.promptTimer);
  },
  methods: {
    async checkInstalledPWA() {
      if (isStandalone()) return;
      if (!("getInstalledRelatedApps" in navigator)) return;
      if (sessionStorage.getItem("scrawl:oiap-dismissed")) return;
      try {
        const apps = await navigator.getInstalledRelatedApps();
        if (apps.some((a) => a.platform === "webapp")) {
          this.openInAppVisible = true;
        }
      } catch {
        // API unavailable in this context — silently skip
      }
    },
    openInApp() {
      // Same-URL replace() is a reload — Chrome never intercepts it as a scope
      // navigation. ?_pwa=<ts> makes it a genuinely different URL; Chrome sees
      // a real navigation within the PWA's scope and opens the installed app.
      // App.vue's stripPwaParam() cleans the param once running in standalone.
      const url = new URL(window.location.href);
      url.searchParams.set("_pwa", Date.now());
      window.location.replace(url.toString());
    },
    dismissOpenInApp() {
      sessionStorage.setItem("scrawl:oiap-dismissed", "1");
      this.openInAppVisible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.home {
  text-align: center;
  font-family: $font-body;

  /* Single coordinate system: a flex column hard-capped to the visible
     viewport (`height: 100dvh` tracks URL-bar show/hide on modern mobile
     browsers). Hero is `flex: 1 1 auto` so it shrinks to give `.bottom`
     the space it needs — `.bottom` is always pinned at the bottom regardless
     of content height, URL-bar state, notch, or PWA chrome. Using `height`
     (not `min-height`) prevents the container from growing taller than the
     viewport, which would push `.bottom` off-screen with no scroll escape
     since `overflow: hidden` kills scrolling. Class is `.home-shell` (not
     `.content`) to escape the global `#app .container .content { position:
     absolute }` rule from main.scss. */
  .home-shell {
    position: relative;
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    /* Soft Y2K candy-sky base. Blobs sit on top providing the gradient
       motion; a faint vertical wash gives depth so the whole shell never
       reads flat. */
    background:
      linear-gradient(180deg, $sky-1 0%, $sky-2 45%, $sky-3 100%);
    color: $ink;
    overflow: hidden;
    padding: 24px 5% calc(16px + env(safe-area-inset-bottom, 0px));
  }

  .bg-blobs {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;

    .blob {
      position: absolute;
      border-radius: 50%;
      filter: blur(70px);
      -webkit-filter: blur(70px);
      will-change: transform;
      mix-blend-mode: screen;
    }

    /* Y2K palette: bubblegum, chrome-blue, mint, lilac.
       Radial gradients (vs. flat fills) keep edges from reading as a
       hard circle once blurred — they fade into the sky. */
    .blob-1 {
      width: 340px; height: 340px;
      background: radial-gradient(circle, $bubblegum-soft 0%, transparent 70%);
      top: -100px; left: -110px;
      opacity: 0.85;
      animation: blob-drift-1 18s ease-in-out infinite;
    }
    .blob-2 {
      width: 300px; height: 300px;
      background: radial-gradient(circle, $chrome-blue 0%, transparent 70%);
      top: 28%; right: -120px;
      opacity: 0.7;
      animation: blob-drift-2 22s ease-in-out infinite;
    }
    .blob-3 {
      width: 380px; height: 380px;
      background: radial-gradient(circle, $mint 0%, transparent 70%);
      bottom: -160px; left: -80px;
      opacity: 0.55;
      animation: blob-drift-3 26s ease-in-out infinite;
    }
    .blob-4 {
      width: 240px; height: 240px;
      background: radial-gradient(circle, #dec8ff 0%, transparent 70%);
      bottom: 18%; right: -90px;
      opacity: 0.7;
      animation: blob-drift-4 20s ease-in-out infinite;
    }
  }

  @keyframes blob-drift-1 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(22px, 14px); } }
  @keyframes blob-drift-2 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-18px, 20px); } }
  @keyframes blob-drift-3 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(16px, -12px); } }
  @keyframes blob-drift-4 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-14px, -16px); } }

  .hero {
    position: relative;
    z-index: 2;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    width: 100%;
    padding-bottom: 2vh;
  }

  /* ─── Prompt pill (rotates through hero prompts) ───
     Mirrors mock's `.home-prompt` (lines 459–468): low-contrast white
     strip, ink-soft text, with a tiny bubblegum-deep star icon glyph
     leading. The .pill-text class is preserved for the JS-driven
     <Transition> to keep cycling prompts unchanged.  */
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: $radius-pill;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow:
      0 6px 14px -8px rgba($shadow-purple, 0.2),
      inset 0 1px 0 #fff;
    font-size: 0.78em;
    font-weight: 600;
    letter-spacing: 0;
    color: $ink-soft;
    line-height: 1.4;
  }

  .pill .ico {
    flex-shrink: 0;
    color: $bubblegum-deep;
    /* SVG is currentColor-driven; bumping size on tighter screens keeps
       the star centered with the prompt text. */
  }

  .pill-text {
    display: inline-block;
    white-space: nowrap;
    color: $ink;
    font-weight: 700;
  }

  /* Prompt transitions (preserved from previous design) */
  .prompt-up-enter-active   { transition: opacity 0.35s ease, transform 0.35s ease; }
  .prompt-up-leave-active   { transition: opacity 0.25s ease, transform 0.25s ease; }
  .prompt-up-enter-from     { opacity: 0; transform: translateY(6px); }
  .prompt-up-leave-to       { opacity: 0; transform: translateY(-6px); }

  .prompt-down-enter-active { transition: opacity 0.35s ease, transform 0.35s ease; }
  .prompt-down-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
  .prompt-down-enter-from   { opacity: 0; transform: translateY(-6px); }
  .prompt-down-leave-to     { opacity: 0; transform: translateY(6px); }

  .prompt-fade-enter-active { transition: opacity 0.4s ease; }
  .prompt-fade-leave-active { transition: opacity 0.3s ease; }
  .prompt-fade-enter-from,
  .prompt-fade-leave-to     { opacity: 0; }

  .prompt-blur-enter-active { transition: opacity 0.4s ease, filter 0.4s ease; }
  .prompt-blur-leave-active { transition: opacity 0.3s ease, filter 0.3s ease; }
  .prompt-blur-enter-from,
  .prompt-blur-leave-to     { opacity: 0; filter: blur(6px); }

  .prompt-pop-enter-active  { transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
  .prompt-pop-leave-active  { transition: opacity 0.25s ease, transform 0.25s ease; }
  .prompt-pop-enter-from    { opacity: 0; transform: scale(0.9); }
  .prompt-pop-leave-to      { opacity: 0; transform: scale(0.94); }

  .prompt-wipe-enter-active { transition: clip-path 0.45s cubic-bezier(0.4, 0, 0.2, 1); }
  .prompt-wipe-leave-active { transition: clip-path 0.35s cubic-bezier(0.4, 0, 0.2, 1); }
  .prompt-wipe-enter-from   { clip-path: inset(0 100% 0 0); -webkit-clip-path: inset(0 100% 0 0); }
  .prompt-wipe-leave-to     { clip-path: inset(0 0 0 100%); -webkit-clip-path: inset(0 0 0 100%); }

  @media (prefers-reduced-motion: reduce) {
    [class*="prompt-"][class$="-enter-active"],
    [class*="prompt-"][class$="-leave-active"] { transition: none; }
    [class*="prompt-"][class$="-enter-from"],
    [class*="prompt-"][class$="-leave-to"]    {
      opacity: 1;
      transform: none;
      filter: none;
      clip-path: none;
      -webkit-clip-path: none;
    }
  }

  /* ─── Diorama (ScrawlDemo component sits here) ─── */
  /* Default scale 0.90 so the rotated phone corners (which extend ~31px past
     the diorama edges at ±15°) clear the page padding. transform-origin
     center-top + negative margin-bottom reclaims the empty layout space
     left by the scale transform. margin-top biases the diorama lower in the
     centered hero stack, so it sits closer to the CTAs without hugging them. */
  .diorama {
    width: 100%;
    margin-top: 60px;
    transform: scale(0.9);
    transform-origin: center top;
    /* (1 - scale) × diorama height 332 ≈ 33 */
    margin-bottom: -33px;
  }

  /* ─── ActMarker — sits inside .bottom, above the CTA stack ─── */
  .act-marker {
    /* Component already centers its pips. .bottom's flex gap (18px) handles
       spacing between this and .cta below. */
    margin: 0;
  }

  .bottom {
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 100%;
  }

  /* ─── CTA stack ───
     Two stacked bubble buttons: primary holographic + ghost glass.
     Mirrors mock's `.bubble-btn` / `.bubble-btn.ghost` (lines 311–355).
     Class names `.btn`, `.btn-primary`, `.btn-secondary` preserved as
     stable selectors. */
  .cta {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .btn {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      height: 52px;
      padding: 0 22px;
      border-radius: $radius-pill;
      font-family: $font-body;
      font-size: 1.05em;
      font-weight: 700;
      letter-spacing: -0.01em;
      text-align: center;
      cursor: pointer;
      text-decoration: none;
      border: none;
      overflow: hidden;
      transition:
        transform $dur-fast $ease-bubble,
        box-shadow $dur-base $ease-soft,
        background-position 0.6s $ease-soft;
    }

    /* ── Primary: flat holographic CTA ──
       V2.1 update: dropped the glossy `::before` top sheen, the layered
       `$shadow-bubble` with insets, and the white text-shadow — those
       are what made it look 3D. The flat treatment matches the rest of
       the V2 surface palette. The continuous diagonal shimmer wipe
       (`::after`) stays. */
    .btn-primary {
      background: $holo-linear;
      background-size: 200% 200%;
      background-position: 0% 50%;
      color: #fff;
      box-shadow: 0 6px 18px -8px rgba($shadow-purple, 0.45);
      letter-spacing: -0.01em;

      /* Diagonal shimmer wipe over the holo gradient — the only animated
         finish on the flat pill. */
      &::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: linear-gradient(110deg, transparent 30%, rgba(255, 255, 255, 0.55) 50%, transparent 70%);
        background-size: 200% 100%;
        background-position: -50% 0;
        pointer-events: none;
        animation: cta-shimmer 5.5s $ease-soft infinite;
      }

      &:hover,
      &:focus-visible {
        background-position: 100% 50%;
        box-shadow: 0 8px 22px -8px rgba($shadow-purple, 0.5);
      }

      &:active {
        transform: scale(0.96);
        box-shadow: 0 3px 8px -5px rgba($shadow-purple, 0.4);
      }
    }

    /* ── Secondary: ghost glass pill ── */
    .btn-secondary {
      background: rgba(255, 255, 255, 0.7);
      color: $ink;
      border: 1px solid rgba(255, 255, 255, 0.9);
      backdrop-filter: $glass-blur;
      -webkit-backdrop-filter: $glass-blur;
      box-shadow:
        0 6px 14px -8px rgba($shadow-purple, 0.4),
        inset 0 1px 0 #fff,
        inset 0 -3px 8px -4px rgba($chrome-blue, 0.3);
      text-shadow: none;

      &:active {
        transform: scale(0.96);
        box-shadow:
          0 3px 8px -8px rgba($shadow-purple, 0.4),
          inset 0 1px 0 rgba(255, 255, 255, 0.85);
      }
    }
  }

  @keyframes cta-shimmer {
    0%       { background-position: 200% 0; }
    55%, 100%{ background-position: -120% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .cta .btn-primary::after { animation: none; }
  }

  .legal-links {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 0.78em;
    font-weight: 500;
    color: $ink-faint;

    a {
      color: $ink-soft;
      text-decoration: none;
      transition: color $dur-fast $ease-soft;

      &:hover { color: $bubblegum-deep; }
    }

    span {
      opacity: 0.6;
      color: $ink-faint;
    }
  }

  /* ──────────────────────────────────────────────
     Responsive scaling
     ──────────────────────────────────────────────
     The hero stacks: Wordmark (~58px) + pill (~36px) + ScrawlDemo (332px),
     plus the bottom block (ActMarker 12 + CTAs 114 + legal 24, plus gaps
     and padding ~210px). At full size it needs ~700px and fits iPhone 14+
     (≥820dvh) comfortably. Smaller phones get progressively scaled down so
     the layout never clips inside `.home-shell { overflow: hidden }`.
     Wordmark size adjusts inside Wordmark.vue's own scoped media queries.

     Breakpoints chosen against real devices (in dvh):
       Pro Max iPhones / Pixel Pro / S Ultra        ≥ 880   (default)
       iPhone 14/15/16 / Pixel 7 / S23              820–879 (default)
       iPhone 13 mini, slim Androids                720–819 (-1 step)
       iPhone SE 3rd gen, foldables closed          620–719 (-2 steps)
       iPhone SE 1st/2nd gen, very small screens    < 620   (-3 steps)
  */

  /* ── Step 1: tighter spacing for slim phones ── */
  @media (max-height: 819px) {
    .hero {
      gap: 12px;
      padding-bottom: 1vh;
    }
    .diorama {
      transform: scale(0.84);
      /* (1 - 0.84) × 332 ≈ 53 */
      margin-bottom: -53px;
    }
  }

  /* ── Step 2: compact phones (iPhone SE 3rd gen, foldables closed) ── */
  @media (max-height: 719px) {
    .hero {
      gap: 10px;
      padding-bottom: 0;
    }
    .pill {
      padding: 7px 14px;
      font-size: 0.85em;
    }
    .diorama {
      transform: scale(0.72);
      /* (1 - 0.72) × 332 ≈ 93 */
      margin-bottom: -93px;
    }
    .cta .btn {
      height: 48px;
      font-size: 1em;
    }
  }

  /* ── Step 3: very compact (iPhone SE 1st/2nd gen, ~568dvh) ── */
  @media (max-height: 619px) {
    .hero {
      gap: 8px;
    }
    .diorama {
      transform: scale(0.58);
      /* (1 - 0.58) × 332 ≈ 139 */
      margin-bottom: -139px;
    }
  }

  /* ── Width: very narrow phones (≤360px wide, e.g. iPhone SE) ── */
  @media (max-width: 359px) {
    .pill {
      padding: 7px 12px;
      font-size: 0.72em;
    }
  }

  /* ── Width: tablets / desktop visiting the PWA — keep mobile-app feel ── */
  @media (min-width: 600px) {
    .hero,
    .bottom {
      max-width: 440px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  /* ── Landscape phones — short and wide. Centre the layout vertically
     and rely on Step 2/3 height scaling for vertical fit. ── */
  @media (max-height: 500px) and (orientation: landscape) {
    .home-shell {
      padding-top: 12px;
    }
    .hero {
      gap: 6px;
      padding-bottom: 0;
    }
  }
}

/* ── Open-in-app strip ────────────────────────────────────────────────────
   Teleported to <body> so it's not clipped by .home-shell { overflow:hidden }.
   Appears at the top of the viewport when getInstalledRelatedApps() confirms
   the PWA is installed and the user is in a browser tab. */
.open-in-app-strip {
  position: fixed;
  top: calc(12px + env(safe-area-inset-top, 0px));
  left: 14px;
  right: 14px;
  z-index: 1002;
  height: 52px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.95);
  border-radius: $radius-bubble;
  box-shadow:
    0 12px 28px -12px rgba($shadow-purple, 0.28),
    inset 0 1px 0 #fff,
    inset 0 -1px 0 rgba($chrome-blue, 0.15);
  font-family: $font-body;
  animation: oap-slide-down 0.38s $ease-bubble both;
}

@keyframes oap-slide-down {
  from { transform: translateY(calc(-100% - 26px)); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

.oap-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $bubblegum;
  box-shadow: 0 0 8px $bubblegum;
}

.oap-text {
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  color: $ink;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;
}

.oap-open {
  flex-shrink: 0;
  height: 30px;
  padding: 0 14px;
  border-radius: $radius-pill;
  background: $holo-linear;
  color: #fff;
  font-family: $font-body;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: -0.005em;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity $dur-fast $ease-soft;

  &:active { opacity: 0.82; }
}

.oap-close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.9);
  color: $ink-soft;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background $dur-fast $ease-soft;

  &:hover { background: $glass-strong; }
  &:active { transform: scale(0.92); }
}

@media (prefers-reduced-motion: reduce) {
  .open-in-app-strip { animation: none; }
}
</style>
